import { useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { Drawer } from "@/components/system";
import {
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
  FormInput,
  FormSelect,
} from "@/components/forms";
import {
  ALLOWED_CATEGORIES,
  type SuggestedActionItem,
} from "@/hooks/use-inbox";

/**
 * BW19 PR-D — inline editor for triage suggestions.
 *
 * Lets the user tweak titles, categories, effort, and deadlines before
 * promoting the suggestions into action_items. No new fields beyond what
 * action_item already supports; this modal stays as light as possible.
 */

export function EditTriageModal({
  open,
  initialItems,
  onClose,
  onSave,
}: {
  open: boolean;
  initialItems: SuggestedActionItem[];
  onClose: () => void;
  onSave: (items: SuggestedActionItem[]) => void;
}) {
  const [items, setItems] = useState<SuggestedActionItem[]>(() => initialItems);

  function update(idx: number, patch: Partial<SuggestedActionItem>) {
    setItems((curr) => curr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function remove(idx: number) {
    setItems((curr) => curr.filter((_, i) => i !== idx));
  }

  function add() {
    setItems((curr) => [
      ...curr,
      {
        title: "",
        category: "admin",
        effort_minutes: 15,
        suggested_deadline: null,
        depends_on: [],
      },
    ]);
  }

  return (
    <Drawer open={open} onClose={onClose} title="Edit triage" size="lg">
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {items.length === 0 && (
          <p style={{ color: "var(--fg-2)", fontSize: 13, margin: 0 }}>
            No items yet. Add one below or close to leave the capture untouched.
          </p>
        )}
        {items.map((item, idx) => (
          <div
            key={idx}
            style={{
              padding: "var(--space-3)",
              background: "var(--bg-2)",
              border: "1px solid var(--hair)",
              borderRadius: "var(--radius-card)",
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-2)",
            }}
          >
            <FormInput
              label="Title"
              value={item.title}
              onChange={(v) => update(idx, { title: v })}
              placeholder="Reply to Maria"
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-2)" }}>
              <FormSelect
                label="Category"
                value={item.category}
                onChange={(v) => update(idx, { category: v })}
                options={ALLOWED_CATEGORIES.map((c) => ({ value: c, label: c }))}
              />
              <FormInput
                label="Effort (min)"
                type="number"
                value={String(item.effort_minutes)}
                onChange={(v) => {
                  const n = Number(v);
                  update(idx, { effort_minutes: Number.isFinite(n) && n > 0 ? n : 15 });
                }}
              />
            </div>
            <FormInput
              label="Deadline"
              type="datetime-local"
              value={
                item.suggested_deadline
                  ? toLocalInput(item.suggested_deadline)
                  : ""
              }
              onChange={(v) =>
                update(idx, {
                  suggested_deadline: v ? new Date(v).toISOString() : null,
                })
              }
            />
            <button
              type="button"
              onClick={() => remove(idx)}
              aria-label="Remove item"
              style={{
                alignSelf: "flex-end",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "6px 10px",
                background: "transparent",
                color: "var(--fg-2)",
                border: "1px solid var(--hair)",
                borderRadius: "var(--radius-card)",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              <Trash2 size={14} />
              Remove
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={add}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            alignSelf: "flex-start",
            padding: "8px 12px",
            background: "var(--chip)",
            color: "var(--fg)",
            border: "1px solid var(--hair)",
            borderRadius: "var(--radius-card)",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          <Plus size={14} /> Add item
        </button>

        <DialogFooter>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton
            onClick={() =>
              onSave(items.filter((it) => it.title.trim().length > 0))
            }
          >
            Save and accept
          </PrimaryButton>
        </DialogFooter>
      </div>
    </Drawer>
  );
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
